import React, { useMemo } from 'react';
import { CalendarDays } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';
import { parseCalendarUrl } from './CalendarEmbedEditor';

const STYLE_OPTIONS = {
  defaultBackground: 'card',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

/**
 * Build the final iframe src for Google Calendar with display options.
 */
const buildGoogleEmbedUrl = (baseUrl, content) => {
  try {
    const url = new URL(baseUrl);
    // Apply display toggle params
    if (content.showTitle === false) url.searchParams.set('showTitle', '0');
    if (content.showNav === false) url.searchParams.set('showNav', '0');
    if (content.showTabs === false) url.searchParams.set('showTabs', '0');
    return url.toString();
  } catch {
    return baseUrl;
  }
};

const CalendarEmbedRenderer = ({ block, theme = {} }) => {
  const { content } = block;
  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const parsed = useMemo(() => parseCalendarUrl(content.calendarUrl), [content.calendarUrl]);
  // Responsive default: taller on desktop, shorter on mobile
  const height = typeof window !== 'undefined' && window.innerWidth < 640 ? 450 : 600;

  const iframeSrc = useMemo(() => {
    if (!parsed) return null;
    if (parsed.provider === 'google') {
      return buildGoogleEmbedUrl(parsed.embedUrl, content);
    }
    return parsed.embedUrl;
  }, [parsed, content]);

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-5xl mx-auto">
        {/* Heading */}
        {content.heading && (
          <h2
            className={`${styles.headingSizeClass} font-bold mb-4 sm:mb-6 ${styles.headingAlignmentClass}`}
            style={{
              color: styles.headingColor,
              fontFamily: styles.headingFont,
            }}
          >
            {content.heading}
          </h2>
        )}

        {/* Calendar embed */}
        {iframeSrc ? (
          <div className="w-full rounded-lg overflow-hidden border border-gray-700/30">
            <iframe
              src={iframeSrc}
              title={content.heading || 'Calendar'}
              width="100%"
              height={height}
              frameBorder="0"
              scrolling="no"
              style={{ border: 0, minHeight: '300px' }}
              className="w-full bg-white"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <CalendarDays size={48} className="mb-3 opacity-50" />
            <p className="text-sm italic">
              Paste a Google Calendar URL to display your schedule
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarEmbedRenderer;
